import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Utils from "../../../../utils/utils";
import { useHasPermission } from "../../../../common/Hooks";
import { Button } from "react-bootstrap";
import Forms from "../../../../services/forms";
import PubSub from "../../../../services/PubSub";
import SearchBar from "../../../controls/SearchBar";
import CreateFormTemplate from "./modals/CreateFormTemplate";
import ListStatus from "../controls/ListStatus";

const FormTemplateList = () => {
    const { objectType, id: deploymentId } = useParams();
    const [translate] = useTranslation();
    const ref = useRef();
    const history = useHistory();
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "forms"
    });

    const [showCreateTemplate, setShowCreateTemplate] = useState(false);

    const [formTemplates, setFormTemplates] = useState(null);
    const [searchPrompt, setSearchPrompt] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasDownloadError, setHasDownloadError] = useState(false);

    const getFormTemplates = useCallback(async () => {
        setIsLoading(true);
        const response = await Forms.getTemplatesUnique({ deploymentId });
        if (!response.error && response.data) {
            setFormTemplates(response.data);
        }
        else {
            setHasDownloadError(true);
        }

        setIsLoading(false);
    }, [deploymentId]);

    useEffect(() => {
        if (!formTemplates) {
            getFormTemplates();
        }
    }, [formTemplates, getFormTemplates]);

    useEffect(() => {
        const subscriptionId = PubSub.subscribe("refresh-form-templates", () => {
            getFormTemplates();
        });

        return () => PubSub.unsubscribe("refresh-form-templates", subscriptionId);
    }, [getFormTemplates]);

    const filterFormTemplates = useCallback((formTemplate) => {
        if (formTemplate.name !== undefined && formTemplate.name.toLowerCase().includes(searchPrompt.toLowerCase())) {
            return true;
        }
        if (formTemplate.description !== undefined && formTemplate.description.toLowerCase().includes(searchPrompt.toLowerCase())) {
            return true;
        }
    }, [searchPrompt]);

    const templateListItems = useMemo(() => formTemplates ? formTemplates.filter((formTemplate) => filterFormTemplates(formTemplate)) : [], [formTemplates, filterFormTemplates]);

    const templateList = useMemo(() => {
        if (templateListItems && templateListItems.length > 0) {

            return (
                <div className="flex-lg-grow-1 overflow-y-lg-auto padded-scrollbar">
                    <div className="list-group">
                        {templateListItems.map((formTemplate, index) => (
                            <div
                                className="list-group-item list-group-item-action small p-3 clickable"
                                onClick={() => { history.push(`/${objectType}/${deploymentId}/forms-templates/${formTemplate.name}/${formTemplate.version}`); }}
                                key={index}
                            >
                                <label className="d-block h6 clickable">{formTemplate.name}</label>
                                {formTemplate.description ? <label className="d-block mt-2 clickable">{formTemplate.description}</label> : null}
                            </div>
                        ))}
                    </div>
                </div>
            )
        }
        else {
            return <ListStatus loading={isLoading} error={hasDownloadError} empty={true} ></ListStatus>;
        }
    }, [templateListItems, objectType, deploymentId, history, isLoading, hasDownloadError]);

    return <>
        {showCreateTemplate &&
            <CreateFormTemplate
                show={showCreateTemplate}
                onCreate={({ name, version }) => { PubSub.publish('refresh-form-templates'); history.push(`/${objectType}/${deploymentId}/forms-templates/${name}/${version}`); setShowCreateTemplate(false) }}
                onClose={() => { setShowCreateTemplate(false) }} />
        }
        <div className="d-flex align-items-center mb-3">
            <SearchBar ref={ref} size="sm" initialValue={searchPrompt} placeholder={translate("OTM_SEARCH")} onChange={(value) => setSearchPrompt(value)} disabled={isLoading} />
            {permissions.update ? <Button size="sm" className="ms-3" onClick={() => { setShowCreateTemplate(true) }} disabled={isLoading}>{translate("OTM_ADD")}</Button> : null}
        </div>
        {templateList}
    </>
}

export default FormTemplateList;